import { t } from "@lingui/core/macro";
import {
  Autocomplete,
  Description,
  EmptyState,
  FieldError,
  Label,
  ListBox,
  SearchField,
  useFilter,
} from "@heroui-v3/react";
import type { ReactNode } from "react";
import { MantineFormValues } from "./shared.types";

export type LimeAutocompleteV3Item = {
  id: string;
  label: string;
  startContent?: ReactNode;
};

export type LimeAutocompleteV3Props = MantineFormValues & {
  label?: ReactNode;
  description?: ReactNode;
  items: LimeAutocompleteV3Item[];
  value?: string | null;
  onChange?: (value: string | null) => void;
  placeholder?: string;
  emptyContentMessage?: string;
  isDisabled?: boolean;
  isRequired?: boolean;
  className?: string;
  fullWidth?: boolean;
  variant?: "primary" | "secondary";
  "data-identifier"?: string;
};

export const LimeAutocompleteV3 = ({
  label,
  description,
  items,
  value,
  onChange,
  placeholder,
  emptyContentMessage = t`Ni rezultatov`,
  error,
  isDisabled,
  isRequired,
  className,
  fullWidth,
  variant,
  "data-identifier": dataIdentifier,
}: LimeAutocompleteV3Props) => {
  const { contains } = useFilter({ sensitivity: "base" });

  return (
    <Autocomplete
      aria-label={typeof label === "string" ? label : "Autocomplete"}
      className={className}
      data-identifier={dataIdentifier}
      fullWidth={fullWidth}
      isDisabled={isDisabled}
      isInvalid={!!error}
      isRequired={isRequired}
      value={value ?? null}
      variant={variant}
      onChange={(key) => onChange?.(key != null ? String(key) : null)}
      placeholder={placeholder}
    >
      {label ? <Label>{label}</Label> : null}
      <Autocomplete.Trigger>
        <Autocomplete.Value />
        <Autocomplete.ClearButton />
        <Autocomplete.Indicator />
      </Autocomplete.Trigger>
      {error ? (
        <FieldError>{error}</FieldError>
      ) : description ? (
        <Description>{description}</Description>
      ) : null}
      <Autocomplete.Popover>
        <Autocomplete.Filter filter={contains}>
          <SearchField autoFocus aria-label={t`Išči`} name="search" variant="secondary">
            <SearchField.Group>
              <SearchField.SearchIcon />
              <SearchField.Input placeholder={t`Išči...`} />
              <SearchField.ClearButton />
            </SearchField.Group>
          </SearchField>
          <ListBox renderEmptyState={() => <EmptyState>{emptyContentMessage}</EmptyState>}>
            {items.map((item) => (
              <ListBox.Item key={item.id} id={item.id} textValue={item.label}>
                {item.startContent}
                {item.label}
              </ListBox.Item>
            ))}
          </ListBox>
        </Autocomplete.Filter>
      </Autocomplete.Popover>
    </Autocomplete>
  );
};
